// AbInboxPanel — Posteingang-Panel des Dashboards (Vorbild OC-Pilot pages/Dashboard.tsx Inbox-
// Teil). Tabs Offen/Erledigt, serverseitige Suche+Pagination (listMailInbox). GET /mail/status ist
// ADMIN-only -> der "Mail-Eingang nicht konfiguriert"-Hinweis ist daher nur sichtbar, wenn
// mailEnabled explizit false ist (vom Elternteil nur fuer Admins ermittelt); fuer Nicht-Admins
// bleibt mailEnabled null und das Panel zeigt einen neutralen leeren Zustand (keine Betriebsinfo
// an Nicht-Admins durchreichen).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Inbox as InboxIcon } from 'lucide-react';
import { Chip, inputCls, Pager, initials, fmtDateTime, mailStatusTone } from '../ui';
import { listMailInbox } from '../../api';

const PAGE_SIZE = 15;

const InboxRow = ({ item, isDarkMode, t, onOpen }) => {
  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const textMuted = isDarkMode ? 'text-white/50' : 'text-slate-500';
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`w-full text-left flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
      >
        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${isDarkMode ? 'bg-white/10 text-white/70' : 'bg-slate-100 text-slate-500'}`}>
          {initials(item.senderAddress)}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-semibold truncate ${textMain}`}>{item.subject || t('ab.shell.mailNoSubject')}</span>
          <span className={`flex items-center gap-1.5 text-xs truncate ${textMuted}`}>
            <span className="truncate">{item.senderAddress || '—'}</span>
            <span aria-hidden="true">·</span>
            <Chip tone={mailStatusTone(item.status)} isDarkMode={isDarkMode}>{t(`ab.shell.mailStatus.${item.status}`)}</Chip>
            <span aria-hidden="true">·</span>
            <span className="flex-shrink-0">{fmtDateTime(item.receivedAt)}</span>
          </span>
        </span>
        {item.documentStatus && (
          <Chip tone="indigo" isDarkMode={isDarkMode}>{t(`ab.status.${item.documentStatus}`)}</Chip>
        )}
      </button>
    </li>
  );
};

const AbInboxPanel = ({ user, isDarkMode, t, mailEnabled, onOpenDocument, onOpenMail, panelRef }) => {
  const [tab, setTab] = useState('open');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [openPage, setOpenPage] = useState(1);
  const [donePage, setDonePage] = useState(1);
  const [openData, setOpenData] = useState(null);
  const [doneData, setDoneData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firstDebounce = useRef(true);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQ(q.trim());
      if (firstDebounce.current) { firstDebounce.current = false; return; }
      setOpenPage(1);
      setDonePage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [q]);

  const loadOpen = useCallback(() => {
    listMailInbox(user, { tab: 'open', q: debouncedQ, page: openPage, pageSize: PAGE_SIZE })
      .then((res) => { setOpenData(res); setError(null); })
      .catch((e) => setError(e.message));
  }, [user, debouncedQ, openPage]);
  const loadDone = useCallback(() => {
    listMailInbox(user, { tab: 'done', q: debouncedQ, page: donePage, pageSize: PAGE_SIZE })
      .then((res) => { setDoneData(res); setError(null); })
      .catch((e) => setError(e.message));
  }, [user, debouncedQ, donePage]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadOpen(), loadDone()]).finally(() => setLoading(false));
  }, [loadOpen, loadDone]);

  const activeData = tab === 'open' ? openData : doneData;
  const activePage = tab === 'open' ? openPage : donePage;
  const setActivePage = tab === 'open' ? setOpenPage : setDonePage;
  const items = Array.isArray(activeData?.items) ? activeData.items : [];
  const total = Number(activeData?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openTotal = openData?.total ?? 0;
  const doneTotal = doneData?.total ?? 0;

  const cardCls = isDarkMode
    ? 'rounded-2xl p-5 bg-white/[0.06] border border-white/[0.12]'
    : 'rounded-2xl p-5 bg-white border border-slate-200 shadow-sm';

  return (
    <div className={cardCls} ref={panelRef}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{t('ab.shell.inboxTitle')}</h3>
        <div className="relative">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ab.shell.inboxSearchPlaceholder')}
            className={`${inputCls(isDarkMode)} pl-9 w-56`} />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {[
          { id: 'open', label: `${t('ab.tabOpen')}${openData ? ` · ${openTotal}` : ''}` },
          { id: 'done', label: `${t('ab.tabDone')}${doneData ? ` · ${doneTotal}` : ''}` },
        ].map(({ id, label }) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all ${tab === id
              ? (isDarkMode ? 'bg-indigo-600/70 text-white border border-indigo-400/30' : 'bg-indigo-600 text-white')
              : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')}`}>
            {label}
          </button>
        ))}
      </div>

      {/* 3-State: Loading / Error / Empty */}
      {loading ? (
        <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>
      ) : error ? (
        <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.shell.inboxLoadError')}: {error}</p>
      ) : items.length === 0 ? (
        <div className={`flex flex-col items-center gap-2 py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          <InboxIcon className="w-8 h-8" />
          {debouncedQ ? (
            <p className="text-sm">{t('ab.shell.inboxEmptySearch', { q: debouncedQ })}</p>
          ) : tab === 'open' ? (
            mailEnabled === false ? (
              <>
                <p className="text-sm">{t('ab.shell.inboxNotConfigured')}</p>
                <p className={`text-xs max-w-xs ${isDarkMode ? 'text-white/30' : 'text-slate-300'}`}>{t('ab.shell.inboxNotConfiguredHint')}</p>
              </>
            ) : (
              <p className="text-sm">{t('ab.shell.inboxEmptyOpen')}</p>
            )
          ) : (
            <p className="text-sm">{t('ab.shell.inboxEmptyDone')}</p>
          )}
        </div>
      ) : (
        <>
          <ul className="space-y-0.5">
            {items.map((item) => (
              <InboxRow key={item.id} item={item} isDarkMode={isDarkMode} t={t}
                onOpen={() => (item.documentId ? onOpenDocument(item.documentId) : onOpenMail(item.id))} />
            ))}
          </ul>
          <Pager page={activePage} totalPages={totalPages} total={total} onPage={setActivePage} isDarkMode={isDarkMode} t={t} />
        </>
      )}
    </div>
  );
};

export default AbInboxPanel;
